import { Box, Card, Typography } from "@mui/material";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { IProfitLossResumedDto } from "../../../../interfaces/dashboard/IProfitLossResumedDto";
import { colorByValue } from "../../../../common/utils/color";
import { formatPriceToDolar } from "../../../../common/utils/number";
import { useThemeMode } from "../../../../context/theme-context";

const DARK_COLORS = [
    '#FFD700', '#4FC3F7', '#81C784', '#FF8A65', '#CE93D8',
    '#F06292', '#4DD0E1', '#AED581', '#FFB74D', '#90A4AE',
];

const LIGHT_COLORS = [
    '#B8860B', '#0288D1', '#388E3C', '#E64A19', '#7B1FA2',
    '#C2185B', '#00838F', '#689F38', '#E65100', '#455A64',
];

function PieTooltip({ active, payload }: any) {
    if (active && payload && payload.length) {
        const d = payload[0].payload;
        return (
            <Box sx={{ bgcolor: 'background.paper', border: '1px solid rgba(255,215,0,0.2)', borderRadius: 1, p: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>{d.walletName}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Valor: {formatPriceToDolar(d.totalAmount)}
                </Typography>
                <Typography variant="body2" color={colorByValue(d.profitLossPercentage)}>
                    Ganhos/Perdas: {d.profitLossPercentage}%
                </Typography>
            </Box>
        );
    }
    return null;
}

export default function ProfitLossChart({ profitLossList }: { profitLossList: IProfitLossResumedDto[] }) {
    const { mode } = useThemeMode();
    const WALLET_COLORS = mode === 'dark' ? DARK_COLORS : LIGHT_COLORS;

    const pieData = profitLossList
        .filter(w => w.totalInvested > 0 || w.totalAmount > 0 || w.totalSelled > 0)
        .map(w => ({ ...w, value: w.totalAmount > 0 ? w.totalAmount : w.totalSelled > 0 ? w.totalSelled : w.totalInvested }));

    return (
        <>
            <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, mb: 2 }}
            >
                Distribuição por Carteira
            </Typography>
            <Card sx={{ border: '1px solid rgba(255,215,0,0.1)', p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%" minHeight={320}>
                        <PieChart key={pieData.map(d => `${d.walletName}:${d.value}`).join('|')}>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="walletName"
                                cx="50%"
                                cy="45%"
                                outerRadius={100}
                                innerRadius={50}
                                paddingAngle={3}
                            >
                                {pieData.map((_, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={WALLET_COLORS[index % WALLET_COLORS.length]}
                                        opacity={0.85}
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<PieTooltip />} />
                            <Legend
                                formatter={(value) => (
                                    <span style={{ fontSize: '0.75rem', color: 'inherit' }}>{value}</span>
                                )}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <Box sx={{ flex: 1, minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Sem dados para exibir
                        </Typography>
                    </Box>
                )}
            </Card>
        </>
    );
}
